import PermissionGate from '@/components/common/PermissionGate';
// ─────────────────────────────────────────────
// KLOTHIQ — Product Detail Page (Dynamic Master Attributes)
// ─────────────────────────────────────────────

import { useState } from 'react';
import { useOperations } from '@/hooks/useOperations';
import { useParams, useNavigate } from 'react-router-dom';
import Link from '@/components/common/AccessLink';
import { useProduct, useDeactivateProduct, ProductVariant } from '@/hooks/useProducts';
import { useProductTypes, useTargetAudiences, useAgeGroups } from '@/hooks/useMasterConfig';
import { useInventory } from '@/hooks/useInventory';
import PageHeader from '@/components/common/PageHeader';
import LoadingState from '@/components/common/LoadingState';
import ErrorState from '@/components/common/ErrorState';
import StatusBadge from '@/components/common/StatusBadge';
import AdjustStockModal from '@/components/inventory/AdjustStockModal';
import EditVariantModal from '@/components/products/EditVariantModal';
import AddVariantModal from '@/components/products/AddVariantModal';
import { Pencil, Package, PlusCircle, Trash2, Tag } from 'lucide-react';
import { toast } from 'sonner';

import { useConfirm } from '@/contexts/ConfirmContext';

export default function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: product, isLoading, error, refetch } = useProduct(id);
  const { data: productTypes } = useProductTypes();
  const { data: targetAudiences } = useTargetAudiences();
  const { data: ageGroups } = useAgeGroups();
  const { data: inventory } = useInventory();
  const deactivate = useDeactivateProduct();
  const confirm = useConfirm();
  const operations = useOperations();

  const [adjustingVariant, setAdjustingVariant] = useState<{ id: string; sku: string; onHand: number; cost: number } | null>(null);
  const [editingVariant, setEditingVariant] = useState<ProductVariant | null>(null);
  const [isAddingVariant, setIsAddingVariant] = useState(false);

  const handleDeleteProduct = async () => {
    if (!id || !product) return;
    const isOk = await confirm({
      title: 'Deactivate Product',
      description: `Deactivate product "${product.name}"? It will be moved to the Recycle Bin.`,
      variant: 'destructive',
      confirmText: 'Deactivate',
    });
    if (!isOk) return;

    try {
      await deactivate.mutateAsync(id);
      toast.success(`Product "${product.name}" moved to Recycle Bin`);
      navigate('/products');
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete product');
    }
  };

  if (isLoading) return <LoadingState message="Loading product…" />;
  if (error) return <ErrorState message={error.message} onRetry={refetch} />;
  if (!product) return null;

  // Resolve Category Name & Code
  const pType = productTypes?.find(
    (pt) => (pt as any).id === product.product_type_id || pt.code === product.product_type_id || pt.name === product.product_type_id
  );
  const pTypeName = pType?.name ?? 'Unclassified';

  const tAudience = targetAudiences?.find(
    (ta) => ta.id === product.target_audience_id || ta.code === product.target_audience_id
  );
  const aGroup = ageGroups?.find(
    (ag) => ag.id === product.age_group_id || ag.code === product.age_group_id
  );

  // Parse attributes object (combining dynamic attributes and legacy fallback)
  const attrsObj: Record<string, any> = product.attributes && typeof product.attributes === 'object' ? { ...product.attributes } : {};
  if (product.team && !attrsObj.Team && !attrsObj.team && !attrsObj.TEAM) attrsObj.Team = product.team;
  if (product.sleeve && !attrsObj.Sleeve && !attrsObj.sleeve && !attrsObj.SLEEVE_TYPE) attrsObj.Sleeve = product.sleeve;
  if (product.material && !attrsObj.Material && !attrsObj.material && !attrsObj.MATERIAL) attrsObj.Material = product.material;

  // Get inventory items for this product's variants safely
  const variants = product.product_variants ?? [];
  const variantIds = new Set(variants.map((v) => v.id));
  const productInventory = (inventory ?? []).filter((i) => variantIds.has(i.variant_id));

  const totalOnHand = productInventory.reduce((sum, i) => sum + i.quantity_on_hand, 0);
  const totalAvailable = productInventory.reduce((sum, i) => sum + i.quantity_available, 0);

  return (
    <div>
      <PageHeader
        title={product.name}
        description={`${product.product_code} · ${pTypeName}`}
        backTo="/products"
        actions={
          <div className="flex items-center gap-2">
            <Link to={`/products/${id}/edit`}
              className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm font-medium hover:bg-accent">
              <Pencil className="h-4 w-4" /> Edit
            </Link>
            <PermissionGate permission="products.delete"><button
              type="button"
              onClick={handleDeleteProduct}
              className="inline-flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive hover:bg-destructive/20"
              title="Delete Product"
            >
              <Trash2 className="h-4 w-4" /> Delete
            </button></PermissionGate>
          </div>
        }
      />

      {/* Product info card */}
      <div className="mb-4 rounded-lg border border-border bg-card p-5 shadow-sm">
        <div className="flex flex-wrap gap-2 mb-4">
          <span className="inline-flex items-center gap-1 rounded bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
            <Tag className="h-3 w-3" /> Category: {pTypeName}
          </span>
          {tAudience && (
            <span className="rounded bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
              Audience: {tAudience.name}
            </span>
          )}
          {aGroup && (
            <span className="rounded bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
              Age: {aGroup.name}
            </span>
          )}
          <StatusBadge status={product.is_active ? 'active' : 'inactive'} />
        </div>

        {/* Dynamic attribute badges/details */}
        <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Product Code</p>
            <p className="mt-1 font-mono font-semibold text-primary">{product.product_code}</p>
          </div>
          {Object.entries(attrsObj).map(([key, val]) => {
            if (!val || typeof val === 'object') return null;
            const displayKey = key.replace(/_/g, ' ').toUpperCase();
            return (
              <div key={key}>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{displayKey}</p>
                <p className="mt-1 font-medium text-foreground">{String(val)}</p>
              </div>
            );
          })}
        </div>

        {product.description && (
          <p className="mt-4 text-sm text-muted-foreground border-t border-border/60 pt-3">{product.description}</p>
        )}
      </div>

      {/* Stock summary */}
      {productInventory.length > 0 && (
        <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
          <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Total On Hand</p>
            <p className="mt-1 text-2xl font-bold text-foreground">{totalOnHand}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Total Available</p>
            <p className="mt-1 text-2xl font-bold text-foreground">{totalAvailable}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Variants</p>
            <p className="mt-1 text-2xl font-bold text-foreground">{variants.length}</p>
          </div>
        </div>
      )}

      {/* Variants table */}
      <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <Package className="h-4 w-4 text-muted-foreground" />
            Variants ({variants.length})
          </h3>
          <PermissionGate permission="products.create"><button
            type="button"
            onClick={() => operations.data?.modules.catalog === 1 ? navigate(`/products/${id}/edit`) : setIsAddingVariant(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground shadow-sm hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <PlusCircle className="h-3.5 w-3.5" />
            Add Variant
          </button></PermissionGate>
        </div>
        {variants.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">No variants added</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-muted/50">
                <tr>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">SKU</th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Size</th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Color / Option</th>
                  <PermissionGate permission="financials.view"><th className="px-4 py-2.5 text-right font-medium text-muted-foreground">Cost (₹)</th></PermissionGate>
                  <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">Price (₹)</th>
                  <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">On Hand</th>
                  <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">Available</th>
                  <PermissionGate permission="financials.view"><th className="px-4 py-2.5 text-right font-medium text-muted-foreground">WAC (₹)</th></PermissionGate>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Status</th>
                  <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {variants.map((v) => {
                  const inv = productInventory.find((i) => i.variant_id === v.id);
                  return (
                    <tr key={v.id} className="hover:bg-muted/20">
                      <td className="max-w-xs break-all px-4 py-2.5 font-mono text-xs font-semibold text-primary">{v.sku}</td>
                      <td className="px-4 py-2.5 font-medium">{v.size}</td>
                      <td className="px-4 py-2.5 text-muted-foreground">{v.color ?? '—'}</td>
                      <PermissionGate permission="financials.view"><td className="px-4 py-2.5 text-right font-mono text-muted-foreground">₹{Number(v.default_cost).toFixed(2)}</td></PermissionGate>
                      <td className="px-4 py-2.5 text-right font-mono font-medium">₹{Number(v.default_price).toFixed(2)}</td>
                      <td className="px-4 py-2.5 text-right">{inv?.quantity_on_hand ?? 0}</td>
                      <td className={`px-4 py-2.5 text-right font-semibold ${(inv?.quantity_available ?? 0) <= 0 ? 'text-destructive' : 'text-foreground'}`}>
                        {inv?.quantity_available ?? 0}
                      </td>
                      <PermissionGate permission="financials.view"><td className="px-4 py-2.5 text-right font-mono text-muted-foreground">
                        ₹{Number(inv?.weighted_avg_cost ?? 0).toFixed(2)}
                      </td></PermissionGate>
                      <td className="px-4 py-2.5">
                        <StatusBadge status={inv?.stock_status ?? 'out_of_stock'} size="sm" />
                      </td>
                      <PermissionGate permission="financials.view"><td className="px-4 py-2.5 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={() =>
                              setAdjustingVariant({
                                id: v.id,
                                sku: v.sku,
                                onHand: inv?.quantity_on_hand ?? 0,
                                cost: Number(v.default_cost ?? 0),
                              })
                            }
                            className="inline-flex items-center gap-1 rounded border border-border bg-background px-2 py-1 text-[11px] font-medium hover:bg-accent"
                            title="Add or Adjust Stock"
                          >
                            <PlusCircle className="h-3 w-3 text-emerald-600" />
                            Stock
                          </button>
                          <button
                            type="button"
                            onClick={() => operations.data?.modules.catalog === 1 ? navigate(`/products/${id}/edit`) : setEditingVariant(v)}
                            className="inline-flex items-center gap-1 rounded border border-border bg-background px-2 py-1 text-[11px] font-medium hover:bg-accent"
                            title="Edit Variant Details"
                          >
                            <Pencil className="h-3 w-3 text-blue-600" />
                            Edit
                          </button>
                        </div>
                      </td></PermissionGate>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Adjust Stock Modal */}
      {adjustingVariant && (
        <AdjustStockModal
          isOpen={!!adjustingVariant}
          onClose={() => setAdjustingVariant(null)}
          variantId={adjustingVariant.id}
          sku={adjustingVariant.sku}
          productName={product.name}
          currentOnHand={adjustingVariant.onHand}
          unitCost={adjustingVariant.cost}
        />
      )}

      {/* Edit Variant Modal */}
      {editingVariant && (
        <EditVariantModal
          isOpen={!!editingVariant}
          onClose={() => setEditingVariant(null)}
          variant={editingVariant}
        />
      )}

      {/* Add Variant Modal */}
      {isAddingVariant && (
        <AddVariantModal
          isOpen={isAddingVariant}
          onClose={() => setIsAddingVariant(false)}
          productId={product.id}
          productCode={product.product_code}
          productName={product.name}
          defaultCost={variants.length > 0 && variants[0] ? Number(variants[0].default_cost) : 0}
          defaultPrice={variants.length > 0 && variants[0] ? Number(variants[0].default_price) : 0}
        />
      )}
    </div>
  );
}
